// Library Methods
import i18next from "i18next";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import { LinkIcon, PlusIcon, VRIcon } from "src/assets/icons";

import patientHeaderImg from "src/assets/images/operating-manual/instructions-for-use/patient-header.png";
import patientHeaderFrImg from "src/assets/images/operating-manual/instructions-for-use/patient-header-fr.png";
import patientExamImg from "src/assets/images/operating-manual/instructions-for-use/patient-exam.png";
import patientExamFrImg from "src/assets/images/operating-manual/instructions-for-use/patient-exam-fr.png";
import addExamModalImg from "src/assets/images/operating-manual/instructions-for-use/add-exam-modal.png";
import addExamModalFrImg from "src/assets/images/operating-manual/instructions-for-use/add-exam-modal-fr.png";
import linkDeviceModalImg from "src/assets/images/operating-manual/instructions-for-use/link-device-modal.png";
import linkDeviceModalFrImg from "src/assets/images/operating-manual/instructions-for-use/link-device-modal-fr.png";
import successToastImg from "src/assets/images/operating-manual/instructions-for-use/success-toast.png";
import successToastFrImg from "src/assets/images/operating-manual/instructions-for-use/success-toast-fr.png";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

const CreateAnExam = () => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Typography fontSize="16px" fontWeight={600}>
        {t("Method 1: Create Exam From Scratch")}
      </Typography>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            1. {t("Create an exam using the ")}
            <IconButton
              sx={{
                width: `${UI_SIZES.iconButtonSize}px`,
                height: `${UI_SIZES.iconButtonSize}px`,
                color: "#FFF",
                backgroundColor: "#202338",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              <PlusIcon />
            </IconButton>
            {t(" Add button")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={currentLang === "fr" ? patientHeaderFrImg : patientHeaderImg}
              width="100%"
              alt="patient-header"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            2.{" "}
            {t(
              "Select the exam(s) you would like to include by pressing the Add button "
            )}
            <IconButton
              sx={{
                width: `${UI_SIZES.iconButtonSize}px`,
                height: `${UI_SIZES.iconButtonSize}px`,
                color: "#FFF",
                backgroundColor: "#202338",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              <PlusIcon />
            </IconButton>{" "}
            {t(
              "for each exam. Note that you can select multiple exams for one session, including multiple of the same type of test."
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={currentLang === "fr" ? addExamModalFrImg : addExamModalImg}
              width="100%"
              alt="add-exam-modal"
            />
          </Box>
        </Grid>
      </Grid>

      <Alert
        variant="info"
        alertContent={t(
          "Tip: If you'd like to do data entry for exams ahead of time, you can update the schedule date for this exam to appear in your dashboard the day of the exam. If you'd like to immediately send the exam to a device, select the device from the drop down menu."
        )}
      />

      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Typography>
          3.{" "}
          {t(
            "Configure your preferences for each exam then press the Save button at the top of the page:"
          )}
        </Typography>
        <Button sx={{ ...ModalCustomConfirm(), pointerEvents: "none" }}>
          {t("Save")}
        </Button>
      </Box>

      <Alert
        variant="warning"
        alertContent={t(
          "The following step only applies if you did not select a device when creating an exam."
        )}
      />

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Typography>4. {t("Click on the Link button:")}</Typography>{" "}
            <Button sx={{ ...ModalCustomConfirm(), pointerEvents: "none" }}>
              <VRIcon />
              <LinkIcon />
            </Button>
          </Box>

          <Typography>
            {t(
              "You can do this from the list of exams, or from the Dashboard page."
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={currentLang === "fr" ? patientExamFrImg : patientExamImg}
              width="100%"
              alt="patient-exam"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            5.{" "}
            {t(
              "If you have multiple devices, select the device you would like to use from the drop-down menu and press the link button:"
            )}{" "}
            <Button
              sx={{
                ...ModalCustomConfirm(),
                pointerEvents: "none",
              }}
            >
              {t("Link")}
            </Button>
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={
                currentLang === "fr" ? linkDeviceModalFrImg : linkDeviceModalImg
              }
              width="100%"
              alt="link-device-modal"
            />
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          textAlign: "center",
          alignItems: "center",
          borderRadius: "8px",
          padding: "20px",
          backgroundColor: "#5B6D7A30",
        }}
      >
        <Typography>
          {t(
            "Note: This green card will appear in the bottom left of your portal if linked successfully."
          )}
        </Typography>
        <Box
          component="img"
          src={currentLang === "fr" ? successToastFrImg : successToastImg}
          alt="success-toast"
          style={{
            display: "flex",
            width: "40%",
            justifyContent: "center",
          }}
        />
      </Box>

      <Box />

      <Typography fontSize="16px" fontWeight={600}>
        {t("Method 2: Use Preset Exam Package")}
      </Typography>

      <Grid container spacing="20px">
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
        >
          <Typography>
            1.{" "}
            {t(
              "Add a preset exam package using the 'quick add' exam package button:"
            )}{" "}
            <Button
              sx={{
                ...ModalCustomCancel(theme),
                pointerEvents: "none",
              }}
              startIcon={<PlusIcon />}
            >
              {t("Test Package")}
            </Button>
          </Typography>

          <Typography>
            <Link
              to="/knowledge-hub/operating-manual/instructions-for-use/configure-settings"
              style={{ color: "#0066CC", textDecoration: "underline" }}
              onClick={() =>
                setTimeout(() => {
                  document
                    ?.getElementById("configure-settings")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100)
              }
            >
              {t("See Configure Account Settings to adjust this template.")}
            </Link>
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={currentLang === "fr" ? patientHeaderFrImg : patientHeaderImg}
              width="100%"
              alt="patient-header"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateAnExam;
